import { useCallback, useEffect, useState } from "react";
import { environment } from "environment/environment";

const ACCOUNT_NAME = "PK";
const UNDERLYING = "NIFTY";

const withQuery = (url) => {
  const params = new URLSearchParams({
    accountName: ACCOUNT_NAME,
    underlying: UNDERLYING,
  });
  return `${url}${url.includes("?") ? "&" : "?"}${params.toString()}`;
};

const readJson = async (response) => {
  if (!response.ok) {
    throw new Error(`Gamma Blast request failed (${response.status})`);
  }
  return response.json();
};

const readOptionalJson = async (response) => {
  if (response.status === 404) return null;
  return readJson(response);
};

/**
 * Reads the latest detector reading, persisted events, and outcome counters.
 * The live Greeks stream remains the source of minute-by-minute updates; this
 * hook refreshes the read-only detector view whenever that stream advances.
 */
export default function useAngelOneGammaBlast(refreshKey) {
  const [state, setState] = useState({
    latest: null,
    events: [],
    stats: null,
    loading: true,
    error: null,
  });

  const load = useCallback(async (signal) => {
    try {
      const [latestResponse, eventsResponse, statsResponse] = await Promise.all([
        fetch(withQuery(environment.angelOneGammaBlastLatestUrl), { signal }),
        fetch(withQuery(environment.angelOneGammaBlastEventsUrl), { signal }),
        fetch(withQuery(environment.angelOneGammaBlastStatsUrl), { signal }),
      ]);
      const [latest, events, stats] = await Promise.all([
        readOptionalJson(latestResponse),
        readJson(eventsResponse),
        readJson(statsResponse),
      ]);
      setState({
        latest: latest && typeof latest === "object" ? latest : null,
        events: Array.isArray(events) ? events : [],
        stats: stats && typeof stats === "object" ? stats : null,
        loading: false,
        error: null,
      });
    } catch (cause) {
      if (cause?.name === "AbortError") return;
      setState((current) => ({ ...current, loading: false, error: cause }));
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    void load(controller.signal);
    return () => controller.abort();
  }, [load, refreshKey]);

  return { ...state, reload: () => load(undefined) };
}
